/* eslint-disable no-restricted-globals */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Paragraph, Typography, FlexGrid, Icon } from '@telus-uds/components-web';
import Success from '@telus-uds/palette-allium/build/web/icons/Success';
import Time from '@telus-uds/palette-allium/build/web/icons/Time';
import ThumbsUp from '@telus-uds/palette-allium/build/web/icons/ThumbsUp';
import Attention from '@telus-uds/palette-allium/build/web/icons/Attention';
import { Chip, ChipIcon, ChipLabel, LinkButton, SpacedRow, Title, WidgetContainer, ItalicDescription } from '../css';

const chipStatusColorMap = {
    'auto-approved': {
        primary: '#D4EDDA',
        secondary: '#155724',
        text: 'Auto-Approved',
        icon: <Icon icon={ThumbsUp} variant={{ size: 'micro' }} />
    },
    'approved': {
        primary: '#D4EDDA',
        secondary: '#155724',
        text: 'Approved',
        icon: <Icon icon={ThumbsUp} variant={{ size: 'micro' }} />
    },
    'pending-approval': {
        primary: '#FFF3CD',
        secondary: '#856404',
        text: 'Pending Approval',
        icon: <Icon icon={Time} variant={{ size: 'micro' }} />
    },
    'new': {
        primary: '#FFF3CD',
        secondary: '#856404',
        text: 'Pending Approval',
        icon: <Icon icon={Time} variant={{ size: 'micro' }} />
    },
    'rejected': {
        primary: '#F8D7DA',
        secondary: '#721C24',
        text: 'Rejected',
        icon: <Icon icon={Attention} variant={{ size: 'micro' }} />
    },
}

const isValueValid = (val) => {
  return (!isNaN(val) && !!val);
}

const DiscountItem = ({
    id,
    status,
    monthly,
    onetime,
    approvedMonthly,
    approvedOnetime,
    overrideMonthly,
    overrideOnetime,
    onOverride,
    onApply,
    applied,
    term,
    commentFromApprover,
    comment,
    formTerm,
    requestedForManagedITServicesValue,
    requestedForOnboardingFeeValue,
    managedItServicesTotal,
    onboardingFee
  }) => {
    const discountStatus = status.toLowerCase();
    const isDiscountCompatibleWithOrder = 
      (requestedForManagedITServicesValue === +(managedItServicesTotal || 0).toFixed(2))
      && (requestedForOnboardingFeeValue === +(onboardingFee || 0).toFixed(2));

    return (
      <WidgetContainer applied={applied}>
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col xs={9}>
              <Title>Discount ID: {id}</Title>
            </FlexGrid.Col>
            <FlexGrid.Col xs={3}>
              {applied && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                  <Icon icon={Success} variant={{ size: 'medium', color: 'success' }} />
                </div>
              )}
            </FlexGrid.Col>
          </FlexGrid.Row>
          <FlexGrid.Row>
            <FlexGrid.Col xs={12}>
              {chipStatusColorMap[discountStatus] && (
                <Chip
                  size="small"
                  bgColor={chipStatusColorMap[discountStatus].primary}
                  textColor={chipStatusColorMap[discountStatus].secondary}
                >
                  <ChipLabel hasIcon>
                    {chipStatusColorMap[discountStatus].text}
                  </ChipLabel>
                  <ChipIcon size={12}>
                    {chipStatusColorMap[discountStatus].icon}
                  </ChipIcon>
                </Chip>
              )}
              <Chip size="small">
                <ChipLabel>{term} term</ChipLabel>
                {term !== formTerm && (
                  <ChipIcon size={12}>
                    <Icon icon={Attention} variant={{ size: 'small' }} />
                  </ChipIcon>
                )}
              </Chip>
              {applied && (
                <Chip size="small" textColor="#4B286D">
                  <ChipLabel hasIcon>Discount Applied to Form</ChipLabel>
                  <ChipIcon size={12}>
                    <Icon icon={Success} variant={{ size: 'small', color: 'success' }} />
                  </ChipIcon>
                </Chip>
              )}
            </FlexGrid.Col>
          </FlexGrid.Row>
          <SpacedRow>
            <>
              <>
                <FlexGrid.Row>
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Typography variant={{ bold: true }}>Requested Monthly Discount:</Typography> ${monthly}
                    </Paragraph>
                  </FlexGrid.Col>
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Typography variant={{ bold: true }}>Requested One-Time Discount:</Typography> ${onetime}
                    </Paragraph>
                  </FlexGrid.Col>
                </FlexGrid.Row>
              </>
              <FlexGrid.Row>
                {isValueValid(approvedMonthly) && (
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Typography variant={{ bold: true }}>Approved Monthly Discount: </Typography>$
                      {approvedMonthly}
                    </Paragraph>
                  </FlexGrid.Col>
                )}
                {isValueValid(approvedOnetime) && (
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Typography variant={{ bold: true }}>Approved One-Time Discount: </Typography>$
                      {approvedOnetime}
                    </Paragraph>
                  </FlexGrid.Col>
                )}
              </FlexGrid.Row>
              <FlexGrid.Row>
                {isValueValid(+overrideMonthly) && (
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Typography variant={{ bold: true }}>Reduced Monthly Discount: </Typography>$
                      {overrideMonthly}
                    </Paragraph>
                  </FlexGrid.Col>
                )}
                {isValueValid(+overrideOnetime) && (
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <Typography variant={{ bold: true }}>Reduced One-Time Discount: </Typography>$
                      {overrideOnetime}
                    </Paragraph>
                  </FlexGrid.Col>
                )}
              </FlexGrid.Row>
              {commentFromApprover &&
                <FlexGrid.Row>
                  <FlexGrid.Col xs={12}>
                    <Paragraph variant={{ bold: true }}>Approver Comment: </Paragraph>
                  </FlexGrid.Col>
                  <FlexGrid.Col xs={12}>
                    <Paragraph>
                      <ItalicDescription>{commentFromApprover}</ItalicDescription>
                    </Paragraph>
                  </FlexGrid.Col>
                </FlexGrid.Row>
              }
            </>
          </SpacedRow>

          <SpacedRow>
            <FlexGrid.Row style={{ marginTop: 20 }}>
              <FlexGrid.Col xs={4}>
                {['approved', 'auto-approved'].includes(discountStatus) && term === formTerm && isDiscountCompatibleWithOrder && (
                  <LinkButton onClick={onOverride}>
                    <Typography>Reduce Discount</Typography>
                  </LinkButton>
                )}
              </FlexGrid.Col>
              {term === formTerm && !['rejected'].includes(discountStatus) && isDiscountCompatibleWithOrder && (
                <FlexGrid.Col xs={8}>
                  <Button
                    onClick={() => onApply(applied ? 'unApply' : 'apply')}
                    variant={{ priority: applied ? 'low' : 'high' }}
                  >
                    {applied ? 'Unapply Discount' : 'Apply Discount'}
                  </Button>
                </FlexGrid.Col>
              )}
            </FlexGrid.Row>
          </SpacedRow>
        </FlexGrid>
      </WidgetContainer>
    );
  };

DiscountItem.propTypes = {
    id: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['approved', 'underApproval', 'rejected']).isRequired,
    monthly: PropTypes.number.isRequired,
    onetime: PropTypes.number.isRequired,
    approvedMonthly: PropTypes.number.isRequired,
    approvedOnetime: PropTypes.number.isRequired,
    overrideMonthly: PropTypes.number.isRequired,
    overrideOnetime: PropTypes.number.isRequired,
    onOverride: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    applied: PropTypes.bool.isRequired,
    term: PropTypes.string.isRequired,
    formTerm: PropTypes.string.isRequired,
    comment: PropTypes.string.isRequired,
    commentFromApprover: PropTypes.string.isRequired,
    requestedForManagedITServicesValue: PropTypes.number.isRequired,
    requestedForOnboardingFeeValue: PropTypes.number.isRequired,
    managedItServicesTotal: PropTypes.number.isRequired,
    onboardingFee: PropTypes.number.isRequired,
};

  export default DiscountItem;